import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Scale } from 'lucide-react';
import { whatsappBalanceService } from '../services/whatsappBalance.service';
import { messageHandler } from '../services/message-handler.service';
import type { WhatsappBalance, WhatsappBalanceFormData } from '../types/whatsappBalance';
import PageHeader from '../components/ui/PageHeader';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import WhatsappBalanceList from '../components/whatsapp/WhatsappBalanceList';
import WhatsappBalanceForm from '../components/whatsapp/WhatsappBalanceForm';

type ModalType = 'create' | 'edit' | '';

interface ModalState {
  type: ModalType;
}

function WhatsappBalances() {
  const [modal, setModal] = useState<ModalState>({ type: '' });
  const [editingBalance, setEditingBalance] = useState<WhatsappBalance | null>(null);
  const queryClient = useQueryClient();

  const { data: balances, isLoading } = useQuery({
    queryKey: ['whatsapp-balances'],
    queryFn: () => whatsappBalanceService.getAll(),
  });

  const createMutation = useMutation({
    mutationFn: (data: WhatsappBalanceFormData) => whatsappBalanceService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whatsapp-balances'] });
      messageHandler.success('Balanceamento criado com sucesso!');
      closeModal();
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: WhatsappBalanceFormData }) =>
      whatsappBalanceService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whatsapp-balances'] });
      messageHandler.success('Balanceamento atualizado com sucesso!');
      closeModal();
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => whatsappBalanceService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whatsapp-balances'] });
      messageHandler.success('Balanceamento excluído com sucesso!');
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const whatsappId = formData.get('whatsapp_id') as string;
    const templateId = formData.get('template_id') as string;
    const assistantId = formData.get('assistant_id') as string;

    const data: WhatsappBalanceFormData = {
      whatsapp_id: parseInt(whatsappId),
      template_id: templateId ? parseInt(templateId) : null,
      assistant_id: assistantId ? parseInt(assistantId) : null,
    };

    if (editingBalance) {
      updateMutation.mutate({ id: editingBalance.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleDelete = (balance: WhatsappBalance) => {
    messageHandler.confirm(
      'Excluir Balanceamento',
      'Tem certeza que deseja excluir este balanceamento?',
      () => deleteMutation.mutate(balance.id)
    );
  };

  const closeModal = () => {
    setModal({ type: '' });
    setEditingBalance(null);
  };

  return (
    <div className="py-6">
      <div className="px-4 sm:px-6 lg:px-8">
        <PageHeader
          title="Balanceamento de Conexões"
          description="Gerencie o balanceamento de conexões com templates e assistentes."
          icon={Scale}
          actions={
            <Button
              onClick={() => {
                setEditingBalance(null);
                setModal({ type: 'create' });
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Novo Balanceamento
            </Button>
          }
        />

        <WhatsappBalanceList
          balances={Array.isArray(balances) ? balances : []}
          onEdit={balance => {
            setEditingBalance(balance);
            setModal({ type: 'edit' });
          }}
          onDelete={handleDelete}
          isLoading={isLoading}
        />

        <Modal
          isOpen={modal.type === 'create' || modal.type === 'edit'}
          onClose={closeModal}
          title={editingBalance ? 'Editar Balanceamento' : 'Novo Balanceamento'}
        >
          <WhatsappBalanceForm
            balance={editingBalance}
            onSubmit={handleSubmit}
            isLoading={createMutation.isPending || updateMutation.isPending}
            onClose={closeModal}
          />
        </Modal>
      </div>
    </div>
  );
}

export default WhatsappBalances;
